import React, { useEffect, useState, useMemo } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ClearIcon from '@mui/icons-material/Clear';
import { GatewaySensorType, SensorLatest, VisibleSubSensor } from '../../../services/api';
import { updateSensorProperties } from '../../../services/apiService';
import SensorDetails from './SensorDetails';
import SensorData from '../SensorData/SensorData';
import { themeProps } from '../../../styles/theme';
import {
  filterHighStrengthGateway,
  GatewayVisibleSubsensors,
  getAllGatewayVisibleSubsensors,
} from '../helpers';
import { useAppSelector } from '../../../state/store';
import { getSensorsById, getSensorsByLocId } from '../../../state/selectors';
import { BacnetState } from '../../../Views/SensorConfig';

interface SensorDetailsContainerProps {
  sensorDetails: SensorLatest | undefined;
  refreshDetails: () => void;
  subSensors: VisibleSubSensor[] | undefined;
  showDialog: () => void;
  bacnetState: BacnetState | undefined;
}

function SensorDetailsContainer({
  sensorDetails,
  refreshDetails,
  subSensors,
  showDialog,
  bacnetState,
}: SensorDetailsContainerProps): JSX.Element {
  const sensorId = sensorDetails?.id ?? '';
  const [changingSensorStatus, setChangingSensorStatus] = useState<boolean>(false);
  const [allGatewayVisibleSubSensors, setAllGatewayVisibleSubSensors] =
    useState<GatewayVisibleSubsensors[]>();

  const sensorsByLocId = useAppSelector(getSensorsByLocId);
  const sensorsById = useAppSelector(getSensorsById);
  const sensorIsGateway = Object.values(GatewaySensorType).includes(
    sensorDetails?.type as GatewaySensorType
  );
  const isEnabledSensor = !sensorIsGateway && sensorDetails?.gateway !== '';
  const locationId = sensorDetails?.location;

  useEffect(() => {
    setAllGatewayVisibleSubSensors([]);
  }, [locationId]);

  const updateSensorStatus = (gatewayId: string) => {
    const gatewayStatus = {
      gateway: gatewayId,
    };
    setChangingSensorStatus(true);

    updateSensorProperties(sensorId, gatewayStatus)
      .then(() => {
        enqueueSnackbar('Request successful, wait a few seconds for changes to take effect.', {
          variant: 'success',
        });

        setTimeout(() => {
          refreshDetails();
          setChangingSensorStatus(false);
        }, 5000);
      })
      .catch((err) => enqueueSnackbar(err.cause, { variant: 'error', persist: true }));
  };

  const updateWithHighStrengthGateway = (allVisibleSubsensors: GatewayVisibleSubsensors[]) => {
    filterHighStrengthGateway(allVisibleSubsensors, sensorId).then((res) => {
      if (res && res.id !== '') {
        updateSensorStatus(res.id);
      } else
        enqueueSnackbar('High Strength Gateway Not Found', { variant: 'error', persist: true });
    });
  };

  const changeSensorStatus = () => {
    closeSnackbar();
    if (!isEnabledSensor && locationId) {
      if (allGatewayVisibleSubSensors && allGatewayVisibleSubSensors.length > 0) {
        updateWithHighStrengthGateway(allGatewayVisibleSubSensors);
      } else {
        getAllGatewayVisibleSubsensors(locationId, sensorsById, sensorsByLocId).then(
          (allVisibleSubsensors) => {
            setAllGatewayVisibleSubSensors(allVisibleSubsensors);
            updateWithHighStrengthGateway(allVisibleSubsensors);
          }
        );
      }
    } else if (isEnabledSensor) {
      const id = '';
      updateSensorStatus(id);
    }
  };

  const hasEnabledSubSensor = useMemo(() => {
    const enabledSubSensors = subSensors?.filter((sensor) => sensor.status === 'enabled');
    if (enabledSubSensors && enabledSubSensors.length > 0) return true;
    return false;
  }, [subSensors]);

  const delBtnTooltip = useMemo(() => {
    let label = 'Delete sensor';
    if (sensorIsGateway && hasEnabledSubSensor) label = 'Base sensor has enabled sub sensors';
    else if (isEnabledSensor) label = 'Sensor is enabled. Disable first to allow deletion';
    return label;
  }, [hasEnabledSubSensor, isEnabledSensor, sensorIsGateway]);

  return (
    <Grid container spacing={4}>
      <Grid size={{ sm: 6, xs: 12 }}>
        <Paper elevation={0}>
          <SensorDetails
            sensorId={sensorId}
            sensorDetails={sensorDetails}
            bacnetState={bacnetState}
          />
          <div style={{ display: 'flex' }}>
            {!sensorIsGateway && (
              <Button
                color="primary"
                variant={themeProps.btnVariant.default}
                style={{ margin: '0 0 10px 15px', padding: '5px 20px' }}
                startIcon={
                  changingSensorStatus ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <PowerSettingsNewIcon />
                  )
                }
                disabled={changingSensorStatus}
                onClick={changeSensorStatus}
              >
                {isEnabledSensor ? 'Disable' : 'Enable'}
              </Button>
            )}
            <Tooltip title={delBtnTooltip} placement="top-start">
              <div>
                <Button
                  color="secondary"
                  variant={themeProps.btnVariant.default}
                  style={{ margin: '0 0 10px 15px', padding: '5px 20px' }}
                  startIcon={<ClearIcon />}
                  onClick={showDialog}
                  disabled={
                    (!sensorIsGateway && isEnabledSensor) ||
                    (sensorIsGateway && hasEnabledSubSensor)
                  }
                >
                  Delete
                </Button>
              </div>
            </Tooltip>
          </div>
        </Paper>
      </Grid>
      <SensorData sensorDetails={sensorDetails} />
    </Grid>
  );
}

export default SensorDetailsContainer;
